"use client";

import { useClimateState } from "./use-climate-state";
import { SectionTitle, Segments, Sources } from "./ui";
import s from "./climate.module.css";

export function ClimateAction() {
  const [target, setTarget] = useClimateState("target", "1.5", ["1.5", "2"]);
  const reduction = target === "1.5" ? 55 : 35;
  return (
    <section className={s.section} id="choices">
      <div className={s.wrap}>
        <SectionTitle
          number="05 / What changes the outcome"
          title="Every fraction of a degree is worth protecting."
        >
          The future is constrained by physics, but it is still affected by
          choices. Cutting emissions limits the scale of losses; adaptation
          helps communities manage the changes already under way.
        </SectionTitle>
        <div className={s.actions}>
          <div className={s.actionsNumber}>
            <Segments
              label="Emissions reduction pathway"
              options={[
                { value: "1.5", label: "1.5°C pathway" },
                { value: "2", label: "2°C pathway" },
              ]}
              value={target}
              onChange={setTarget}
            />
            <div className={`${s.bigNumber} mt-6`} aria-live="polite">
              −{reduction}%
            </div>
            <p className={s.small}>
              Annual global greenhouse-gas emissions in 2035 versus 2019, for
              the selected pathway in UNEP’s 2025 assessment.
            </p>
            <div className={s.riskRow}>
              <div className={s.riskLabel}>
                <span>2019 emissions</span>
                <span>100%</span>
              </div>
              <div className={s.riskTrack}>
                <div
                  className={s.riskBar}
                  data-adapted="true"
                  style={{ width: "100%" }}
                />
              </div>
              <div className={`${s.riskLabel} mt-4`}>
                <span>2035 pathway level</span>
                <span>{100 - reduction}%</span>
              </div>
              <div
                className={s.riskTrack}
                role="img"
                aria-label={`2035 emissions level: ${100 - reduction} per cent of 2019`}
              >
                <div
                  className={s.riskBar}
                  data-adapted="true"
                  style={{ width: `${100 - reduction}%` }}
                />
              </div>
            </div>
          </div>
          <div className={s.actionList}>
            <div>
              <h3>Clean power, with the systems to deliver it.</h3>
              <p>
                Expand low-carbon electricity, grids and storage. Electrify
                transport and heating while improving efficiency, so rising
                energy demand need not mean rising fossil-fuel emissions.
              </p>
            </div>
            <div>
              <h3>Cut methane and protect carbon stores.</h3>
              <p>
                Reduce fossil-fuel leaks and other methane sources, and protect
                forests, wetlands and soils. Faster emissions reductions limit
                the warming that drives feedbacks.
              </p>
            </div>
            <div>
              <h3>Adapt the season, not just the snow.</h3>
              <p>
                Diversify mountain livelihoods, reduce water and energy
                pressure, and plan for shorter or less reliable seasons.
                Snowmaking has physical and environmental limits.
              </p>
            </div>
            <div>
              <h3>Give coasts and ecosystems room.</h3>
              <p>
                Protect habitats, improve wastewater systems and plan for
                changing shorelines. Local resilience matters alongside global
                emissions cuts.
              </p>
            </div>
          </div>
        </div>
        <Sources ids={["policy", "mountains", "oceanLife"]} />
      </div>
    </section>
  );
}
